import { useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';

import SearchableStringListDropdown from '../Filters/SearchableStringListDropdown';
import { DataSource } from '../../types/Data/DataSource';

const cookies = new Cookies();

type Props = {
    dataSourceList: DataSource[];
    cookiesName: string;
    selectionHandler?: (selection: string | undefined) => void;
};

const DataSourceDropdown = ({ dataSourceList, cookiesName, selectionHandler }: Props) => {
    const [selectedDataSource, setSelectedDataSource] = useState<string | undefined>(undefined);

    // Build label/value mapping once
    const dataSourceOptions = useMemo(
        () => dataSourceList.map(ds => ({
            label: ds.name,
            value: ds.externalId
        })),
        [dataSourceList]
    );

    // The dropdown only takes labels (strings)
    const optionLabels = useMemo(() => dataSourceOptions.map(o => o.label), [dataSourceOptions]);

    // Set default value (load from cookie)
    useEffect(() => {
        const saved = cookies.get(cookiesName);

        // Remove invalid cookie value
        if (saved && !dataSourceList.find(x => x.externalId === saved)) {
            cookies.remove(cookiesName, { path: '/' });
            setSelectedDataSource(undefined);
            return;
        }

        if (saved) {
            setSelectedDataSource(saved);
            selectionHandler?.(saved);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once on mount

    const handleChange = (label: string | undefined) => {
        // Map back from label to value (externalId)
        const match = dataSourceOptions.find(o => o.label === label);
        const newValue = match?.value;

        setSelectedDataSource(newValue);
        selectionHandler?.(newValue);
        cookies.set(cookiesName, newValue ?? '', { path: '/', maxAge: 2592000, sameSite: 'lax' }); // 30 days
    };

    return (
        <SearchableStringListDropdown
            label="Data Source"
            options={optionLabels}
            defaultValue={
                dataSourceOptions.find(o => o.value === selectedDataSource)?.label
            }
            onChange={handleChange}
            placeholder="Select data source..."
            inputPlaceholder="Type to search..."
            noResultsText="No matching data sources"
            clearable
        />
    );
};

export default DataSourceDropdown;
